/**
 * La ricevuta di sincronizzazione (OTTO.md §6).
 *
 * "Otto non conferma «salvato». Conferma «arrivato»."
 *
 * È la funzione che fa la differenza rispetto a chiunque altro, quindi è anche
 * il componente da cui non si toglie niente: una riga per canale, uno stato
 * leggibile, l'ora, e — quando qualcosa va storto — il motivo in italiano con
 * la correzione proposta.
 */

import { AlertTriangle, Check, Clock, Send } from "lucide-react";
import {
  CHANNEL_LABELS,
  type ReceiptChannel,
  type SyncReceipt,
} from "@otto/shared";
import { cx, StatusDot } from "../ui/primitives.tsx";

const ICONS = {
  confermato: Check,
  inviato: Send,
  in_attesa: Clock,
  fallito: AlertTriangle,
} as const;

const TONES = {
  confermato: "text-ok",
  inviato: "text-ink-faint",
  in_attesa: "text-wait",
  fallito: "text-danger",
} as const;

const LABELS = {
  confermato: "confermato",
  inviato: "inviato",
  in_attesa: "in attesa",
  fallito: "non riuscito",
} as const;

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  return new Intl.DateTimeFormat("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  }).format(date);
}

export function Receipt({
  receipt,
  compact,
}: {
  receipt: SyncReceipt;
  compact?: boolean;
}) {
  return (
    <div className={cx("flex flex-col", compact ? "gap-1.5" : "gap-2")}>
      {receipt.channels.map((channel) => (
        <ReceiptRow key={channel.channel} channel={channel} />
      ))}
    </div>
  );
}

function ReceiptRow({ channel }: { channel: ReceiptChannel }) {
  const Icon = ICONS[channel.status];

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2.5 text-[13px]">
        <Icon
          size={15}
          className={cx("shrink-0", TONES[channel.status])}
          strokeWidth={2.4}
        />
        <span className="font-semibold text-ink">
          {CHANNEL_LABELS[channel.channel]}
        </span>
        <span className={cx("flex-1", TONES[channel.status])}>
          {LABELS[channel.status]}
        </span>
        {channel.at && channel.status !== "in_attesa" ? (
          <span className="tabular-nums text-ink-faint">
            {formatTime(channel.at)}
          </span>
        ) : null}
        {channel.status === "in_attesa" ? <StatusDot tone="wait" /> : null}
      </div>

      {channel.errorMessage ? (
        <div className="ml-[26px] rounded-2xl bg-danger-soft px-3 py-2">
          <p className="text-[13px] leading-relaxed text-danger">
            {channel.errorMessage}
          </p>
          {channel.suggestedFix ? (
            <p className="mt-1 text-[12px] leading-relaxed text-ink-soft">
              {channel.suggestedFix}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* La nota non è un errore: è il limite noto del gestionale, detto com'è. */}
      {channel.note ? (
        <p className="ml-[26px] text-[12px] leading-relaxed text-ink-faint">
          {channel.note}
        </p>
      ) : null}
    </div>
  );
}
